import { Box, Button, IconButton, Toolbar, Typography } from '@mui/material';
import ContentDrawer from 'components/@admin/@content/ContentDrawer';
import ComposedIcon from 'components/common/ComposedIcon';
import DeleteDialog from 'components/common/DeleteDialog';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import AddBoldIcon from 'mdi-react/AddBoldIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import EditIcon from 'mdi-react/EditIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useDeleteContentMutation, useListContentsQuery } from 'store/app/entities/ContentsSlice';
import { I18n } from 'utils/i18n';
import useOpenClose from 'utils/useOpenClose';

const defaultSortModel = [{ field: 'createdAt', sort: 'desc' }];

const AdminContentScene = () => {
  /** `null` = drawer closed; `{}` = create; row object = edit */
  const [editingContent, setEditingContent] = useState(null);
  const [isDeleteConfirmOpen, openDeleteConfirm, closeDeleteConfirm, { contentToRemove }] =
    useOpenClose();
  // Match OmmnioDataGrid default pageSize. Parent perPage must not stay 0: the grid only
  // notifies onPageSizeChange when newPageSize > 1, and autoPageSize can be 0–1 on first layout.
  const [perPage, setPerPage] = useState(10);
  const [sortModel, setSortModel] = useState(defaultSortModel);
  const [filterModel, setFilterModel] = useState(undefined);
  const [page, setPage] = useState(0);
  const search = filterModel?.quickFilterValues?.join(' ') ?? '';
  const sortField = sortModel?.[0]?.field || 'createdAt';
  const sortDir = sortModel?.[0]?.sort || 'desc';
  const queryArg = useMemo(
    () => ({
      search,
      page: page + 1,
      perPage,
      sortBy: sortField,
      sort: sortDir,
    }),
    [page, perPage, search, sortField, sortDir],
  );
  const { data = {}, isLoading } = useListContentsQuery(queryArg);
  const [deleteContent] = useDeleteContentMutation();

  const confirmDelete = () => {
    closeDeleteConfirm();
    deleteContent(contentToRemove._id);
  };

  const columns = [
    {
      field: 'name',
      flex: 1,
      editable: false,
      headerName: I18n.t('AdminContentScene.DocumentsTable.Name'),
    },
    {
      field: 'createdAt',
      type: 'string',
      width: 160,
      editable: false,
      renderCell: params => (params.value != null ? moment.unix(params.value).format('L LT') : '—'),
      headerName: I18n.t('AdminContentScene.DocumentsTable.CreatedAt'),
    },
    {
      field: 'actions',
      type: 'actions',
      align: 'right',
      headerAlign: 'right',
      minWidth: 120,
      maxWidth: 160,
      editable: false,
      sortable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setEditingContent(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton
            sx={{ color: 'danger' }}
            onClick={() => {
              openDeleteConfirm({ contentToRemove: params.row });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
      headerName: I18n.t('AdminContentScene.DocumentsTable.Actions'),
    },
  ];

  const rows = data?.contents || [];

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Toolbar
        sx={{
          minHeight: '0 !important',
          overflow: 'hidden',
          boxShadow: 'inset 0 -0.5px 0 0 rgba(2, 2, 3, 0.2);',
          '& .MuiButton-root': {
            borderRadius: 0,
            minHeight: 49,
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Button variant="text" color="secondary" onClick={() => setEditingContent({})}>
          <ComposedIcon size={18} icon={<PencilIcon />} extraIcon={<AddBoldIcon />} />
          {I18n.t('SuperAdminMenu.New content')}
        </Button>
      </Toolbar>
      <Box sx={{ padding: 2, paddingBottom: 5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h3">{I18n.t('SuperAdminMenu.content')}</Typography>
        <OmmnioDataGrid
          loading={isLoading}
          sx={{
            marginTop: 2,
            '& .MuiDataGrid-columnHeader[data-field="actions"]': {
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                justifyContent: 'flex-end',
              },
            },
          }}
          columns={columns}
          rows={rows}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          getRowId={r => r._id}
          sortingMode="server"
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          onSortModelChange={setSortModel}
          onPageChange={setPage}
          onPageSizeChange={setPerPage}
          disableEvenOdd
          defaultSortModel={defaultSortModel}
        />
      </Box>
      <ContentDrawer
        content={editingContent}
        isOpen={editingContent !== null}
        onClose={() => setEditingContent(null)}
      />
      <DeleteDialog
        message={I18n.t('AdminContentScene.ConfirmDelete', {
          name: contentToRemove?.name,
        })}
        typeBack={contentToRemove?.name}
        isOpen={isDeleteConfirmOpen}
        onConfirm={confirmDelete}
        onClose={closeDeleteConfirm}
      />
    </Box>
  );
};

export default AdminContentScene;
